// Route-based code-splitting helpers — LOCAL implementation, not a re-export.
//
// The canonical logic lives in `@symbo-ls/workspace-shared/pages/lazyPage.js`
// (full usage docs + the detached-node-race rationale are there). This file
// used to delegate to it at call time (`_wsShared.pages.lazyPage(...args) :
// ''`), which is a stub-phantom-value under frank's publish serializer: ANY
// bag value that is the RESULT OF CALLING a function obtained through a
// stubbed `@symbo-ls/*` import is tainted, even when the loader argument is
// this project's own code — every route wired through that ternary published
// as the literal `""` (WS-SHARED-LAZYPAGE-HOLLOW-ROUTES-SWITCH-1, 172
// measured hollow values). Frank's own test proves the fix shape
// (smbls/plugins/frank/__tests__/stubPhantomValueGate.test.js test (ix)): a
// route value must be a LOCAL object literal, defined in this project's own
// source, whose `onCreate` dynamic-`import()`s the shared page at RUNTIME —
// never a call routed through an external stub at module-eval time. So this
// file carries the real implementation locally instead of delegating.
// The three loader-failure fallbacks read the SHARED chrome key
// `shell.errorState.lazyPage` — byte-identical copy to
// @symbo-ls/workspace-shared/pages/lazyPage.js's own fallback, and this
// package's manifest declares the `shell` namespace, so duplicating the same
// sentence under `workspace.*` would only give a translator two places to
// disagree.
export const lazyPageFoldSharedLibContext = (root, lib) => {
  const ctx = root.context
  if (lib?.components && ctx?.components) {
    for (const k in lib.components) {
      if (!ctx.components[k]) ctx.components[k] = lib.components[k]
    }
  }
  if (lib?.functions && ctx?.functions) {
    for (const k in lib.functions) {
      if (!ctx.functions[k]) ctx.functions[k] = lib.functions[k]
    }
  }
  if (lib?.state) {
    const seed = {}
    for (const k in lib.state) if (root[k] === undefined) seed[k] = lib.state[k]
    if (Object.keys(seed).length) root.update?.(seed)
  }
}

// THE HOST HOLDS THE MAIN LANDMARK (WS-SHELL-APP-ROUTES-NO-MAIN-LANDMARK-1).
// `el.set` mounts the loaded page INSIDE its placeholder. As the shell's route
// content the placeholder carries the route's landmark — context.js stamps
// every route entry with role main + #main-content (functions/
// anchorMainContent.js) — and inside a PageSlot pane (the host PageSlot stamps
// `_slotPaneKey` on) the pane is the labelled region. Either way a page that
// names its own `main` tag, `main` role or `main-content` id nests a second
// main landmark with a duplicate id (/add-app on dev 2026-10-24: two mains,
// two #main-content), so those three step back and the page keeps everything
// else. A placeholder that holds no landmark (a standalone app's) keeps the
// page's own.
export const lazyPageUnanchor = (def, host) => {
  if (!def || typeof def !== 'object' || !host) return def
  let held = host.role === 'main'
  for (let p = host.parent, i = 0; !held && p && i < 4; p = p.parent, i++) {
    if (p._slotPaneKey) held = true
  }
  if (!held) return def
  const own = {}
  if (def.tag === 'main') own.tag = 'div'
  if (def.role === 'main') own.role = null
  if (def.id === 'main-content') own.id = null
  if (def.attr && typeof def.attr === 'object' && def.attr.id === 'main-content') {
    own.attr = { ...def.attr, id: null }
  }
  return { ...def, ...own }
}

// WHILE THE CHUNK LOADS (DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): every
// placeholder below paints the house page skeleton as its content — the
// `PageSkeleton` the PageSlot already shows while a page mounts (canvas
// components/PageSlot.js) — and `el.set` swaps the real page in over it. A
// placeholder used to be an EMPTY box for the whole chunk load, so a window
// opened on a lazy route showed a blank sheet, and a full-page visit a blank
// page, until the chunk arrived.
//
// PREFETCH: `scope.prefetch(scope)` starts the same chunk load early, without
// mounting anything — the shell calls it when a pointer rests on (or focus
// reaches) something that opens the route (functions/slotPrefetch.js). The
// module system caches the import, so the mount's own loader then resolves at
// once.
//
// THE LOADERS TRAVEL IN `scope` (FA205). Every handler here reads its loader
// as `el.scope.<name>` (prefetch: its `scope` argument), never as the factory
// parameter: a closure over a factory parameter does not survive frank's
// serialization, and on a published shell `loader` / `ctxLoader` /
// `pageLoader` resolved to nothing — every lazy route stayed on its skeleton
// and logged the load failure (frank verify, 15 unresolvable-identifier lines
// on pages./admin/*, 2026-10-21). `scope:` travels with the emitted page
// (canvas/pages/lazyPage.js, WS-CANVAS-LAZYPAGE-SCOPE-LOADER-UNDEFINED-1).
export const lazyPage = (loader, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  content: { extends: 'PageSkeleton' },
  scope: { loader, prefetch: (scope) => scope.loader() },
  onCreate: async (el) => {
    if (el.__lazyLoaded) return
    el.__lazyLoaded = true
    // Late-resolve guard: the loader can resolve after the router (or the
    // AppWindow PageSlot) navigated away and DISPOSED this placeholder —
    // dispose() clears el.__ref (and el.node); never swap then. NOT
    // connected YET is not gone: smbls 3.14.84x builds an app's whole first
    // view detached and attaches it in one insertion (firstRender), so a
    // loader that settles on the next microtask (the sign-in routes'
    // Promise.resolve) lands before that. The old `!el.node.isConnected`
    // guard read it as gone and kept the signed-out /login on its skeleton
    // forever (staging + dev, 2026-10-21). el.whenConnected runs the swap
    // now when connected, else in the task that inserts the node — so it
    // lands in the first paint — and never for a disposed element.
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const page = await el.scope.loader()
      if (!page) throw new Error('loader resolved empty')
      _place(lazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
    } catch (err) {
      console.error('[lazyPage] failed to load route module:', err && err.message)
      _place({
        tag: 'div',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})

export const lazySharedLib = (ctxLoader, pickPage, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  content: { extends: 'PageSkeleton' },
  scope: { ctxLoader, pickPage, prefetch: (scope) => scope.ctxLoader() },
  onCreate: async (el) => {
    if (el.__lazyLibLoaded) return
    el.__lazyLibLoaded = true
    // The late-resolve guard of lazyPage above: never for a disposed
    // placeholder (el.__ref cleared), and a first view that is not attached
    // YET swaps when it is (el.whenConnected).
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const mod = await el.scope.ctxLoader()
      const lib = mod && mod.default ? mod.default : mod
      // A resolved context with neither components nor functions is a
      // stale/mismatched bundle — fail into the catch (Refresh prompt)
      // instead of silently add-missing-merging nothing and rendering an
      // empty shell.
      if (!lib || (!lib.components && !lib.functions)) {
        throw new Error(
          'shared-lib context resolved with no components/functions — stale/mismatched bundle (rebuild needed)'
        )
      }
      const root = el.getRoot?.() || el
      lazyPageFoldSharedLibContext(root, lib)
      const page = el.scope.pickPage(lib)
      if (!page) throw new Error('shared-lib page resolved empty')
      _place(lazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
    } catch (err) {
      el.__lazyLibLoaded = false
      console.error('[lazySharedLib] load failed:', err && err.message)
      _place({
        tag: 'div',
        role: 'alert',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})

export const lazySharedLibPage = (ctxLoader, pageLoader, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  content: { extends: 'PageSkeleton' },
  scope: {
    ctxLoader,
    pageLoader,
    prefetch: (scope) => Promise.all([scope.ctxLoader(), scope.pageLoader()])
  },
  onCreate: async (el) => {
    if (el.__lazyLibPageLoaded) return
    el.__lazyLibPageLoaded = true
    // The late-resolve guard of lazyPage above (disposed → never; not
    // attached yet → when it is).
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const mod = await el.scope.ctxLoader()
      const lib = mod && mod.default ? mod.default : mod
      const root = el.getRoot?.() || el
      lazyPageFoldSharedLibContext(root, lib)
      const page = await el.scope.pageLoader()
      if (!page) throw new Error('page loader resolved empty')
      _place(lazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
    } catch (err) {
      console.error('[lazySharedLibPage] load failed:', err && err.message)
      _place({
        tag: 'div',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})

// A LOCAL route page whose module also defines its own page-local components
// (row/card/field components the page body references by string extends).
// Those used to be re-exported through components/index.js so the strings
// resolve - which put the WHOLE page module in the shell's eager entry. This
// folds the module's PascalCase exports (its components; page exports are
// camelCase) into the live context on first mount, add-missing, BEFORE the
// page is set, so every string extend resolves exactly as before.
// __tests__/pageComponentRegistration.test.js reads the same convention and
// __tests__/entryBoundary.test.js keeps the module out of the entry.
export const lazyPageWithComponents = (loader, pageKey, metadata) => ({
  height: '100%',
  minHeight: '100%',
  metadata,
  content: { extends: 'PageSkeleton' },
  // The module, the page's export name and the prefetch ride in `scope` —
  // see lazyPage above (a closure over `loader` / `pageKey` does not survive
  // frank's serialization).
  scope: { loader, pageKey, prefetch: (scope) => scope.loader() },
  onCreate: async (el) => {
    if (el.__lazyLibLoaded) return
    el.__lazyLibLoaded = true
    const _place = (def) => {
      if (!el.__ref) return
      if (typeof el.whenConnected === 'function') el.whenConnected(() => el.set(def))
      else el.set(def)
    }
    try {
      const m = await el.scope.loader()
      const components = {}
      for (const k in m || {}) {
        const v = m[k]
        if (/^[A-Z]/.test(k) && v && typeof v === 'object') components[k] = v
      }
      const root = el.getRoot?.() || el
      lazyPageFoldSharedLibContext(root, { components })
      const page = m && m[el.scope.pageKey]
      if (!page) throw new Error('page module resolved no ' + el.scope.pageKey)
      _place(lazyPageUnanchor({ ...page, height: '100%', minHeight: '100%' }, el))
    } catch (err) {
      el.__lazyLibLoaded = false
      console.error('[lazyPageWithComponents] load failed:', err && err.message)
      _place({
        tag: 'div',
        role: 'alert',
        padding: 'B',
        color: 'caption',
        text: '{{ shell.errorState.lazyPage | polyglot }}'
      })
    }
  }
})
